<style lang="scss">
.zoezi-show-payment-dialog {
    .list-unstyled {
        list-style: none;
    }

    tr.empty-row {
        height: 10px;
    }

    table.table-header tr td:last-child {
        padding-left: 10px;
    }

    table.table-header tr td:first-child, .bold {
        font-weight: bold;
    }
}

</style>

<template>
    <div>
        <v-expansion-panels v-model="expandedPanel">
            <v-expansion-panel class="no-elevation" >
                <v-expansion-panel-header>
                    <div class="zoezi-activity-section">
                        <h3>{{$translate('Coming payments')}}</h3>
                        <small>{{$translate('Your debts are listed here')}}</small>
                    </div>
                    <v-badge color="grey" :content="String(debts.length)"></v-badge>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                    <div v-if="debts.length == 0">
                        {{$translate('No coming payments')}}
                    </div>
                    <div class="zoezi-card-default zoezi-card-horizontal z-clickable" v-for="debt in debts" :key="debt.id" @click="showPayment(debt)" :class="Date.newFull(debt.date).isPastDate() ? 'animate__animated animate__pulse animate__infinite' : ''">
                        <div class="z-card-image" :style="{'background-image': 'url(' + $api.getAssetUrl('/assets/img/cash.png') + ')' }">
                            <div class="z-card-image-time" style="background-color: rgba(192,160,0,0.7)">{{(debt.leftToPayWithFees || debt.amount) | price}}</div>
                            <div class="z-card-image-small" style="background-color: rgba(192,160,0,0.7)">{{Date.newFull(debt.date).isPastDate() ? 'Nu' : Date.formatFullShorter(debt.date)}}</div>
                        </div>
                        <div class="z-card-content">
                            <strong>{{debt.name}}</strong><br>
                            <div v-if="debt.dateInfo">{{debt.dateInfo}}: {{$translate(debt.date.getWeekDayString())}} {{debt.date.getDate()}} {{$translate(debt.date.getMonthName())}} {{debt.date.getFullYear()}}</div>
                            <div>{{(debt.leftToPayWithFees || debt.amount) | price}}</div>
                        </div>
                        <div class="z-card-action pt-2 px-2">
                            <v-btn block class="primary my-2 zoezi-button-thin" @click.stop="pay(debt)" v-if="debt.canPay">{{$translate('Pay')}}</v-btn>
                            <v-btn block class="primary my-2 zoezi-button-thin" @click.stop="showPayment(debt)">{{$translate('Info')}}</v-btn>
                        </div>
                    </div>
                </v-expansion-panel-content>
            </v-expansion-panel>
        </v-expansion-panels>

        <v-dialog v-if="showPayDialog" v-model="showPayDialog" scrollable max-width="850px" :fullscreen="$vuetify.breakpoint.mobile">
            <v-card tile elevation="0" class="z-no-x-scroll">
                <v-card-title class="headline pa-2 primary primarytext--text d-flex fill-width">
                    <v-spacer />
                    <v-btn icon @click="showPayDialog = false"><v-icon class="primary primarytext--text">mdi-close</v-icon></v-btn>
                </v-card-title>
                <zoezi-checkout class="zoezi-checkout" :items="payments" @almostdone="showPaymentDialog=false">
                    <template #donetext>
                        <div><span class="font-weight-bold">{{$translate('Thank you for your payment!')}}</span></div>
                    </template>
                    <template #donesummarytitle>
                        <div class="d-flex font-weight-bold">
                            <div class="flex-grow-1">{{$translate('You have paid')}}</div>
                        </div>
                    </template>
                </zoezi-checkout>
            </v-card>
        </v-dialog>

        <v-dialog v-if="showPaymentDialog" v-model="showPaymentDialog" scrollable max-width="850px">
            <v-card tile elevation="0" class="z-no-x-scroll zoezi-show-payment-dialog">
                <v-card-title class="headline pa-2 primary primarytext--text d-flex fill-width">
                    {{getMethodName(showingPayment)}}
                    <v-spacer />
                    <v-btn icon @click="showPaymentDialog = false"><v-icon class="primary primarytext--text">mdi-close</v-icon></v-btn>
                </v-card-title>

                <v-card-text>
                    <v-row>
                        <v-col md=6 cols=12>
                            <table class="table-header">
                                <tr><td>{{'Payment method' | translate}}</td><td>{{getMethodName(showingPayment)}}</td></tr>
                                <tr><td>{{'Status' | translate}}</td><td>{{showingPayment.status.capitalize() | translate}}</td></tr>
                                <tr v-if="showingPayment.dateInfo"><td>{{showingPayment.dateInfo | translate}}</td><td>{{showingPayment.date.yyyymmdd()}}</td></tr>
                                <tr class="empty-row"></tr>
                                <tr v-if="showingPayment.bankgiroNumber"><td>{{'BG-account' | translate}}</td><td>{{formatBankgiroNumber(showingPayment.bankgiroNumber)}}</td></tr>
                                <tr v-if="showingPayment.referenceNumber"><td>{{'OCR-number' | translate}}</td><td>{{showingPayment.referenceNumber}}</td></tr>
                                <tr><td>{{'Amount' | translate}}</td><td>{{showingPayment.amount | price}}</td></tr>
                                <tr v-if="showingPayment.reminderFee"><td>{{'Reminder fee' | translate}}</td><td>{{showingPayment.reminderFee.amount | price}}</td></tr>
                                <tr v-if="showingPayment.reminderRate"><td>{{'Reminder rate' | translate}}</td><td>{{showingPayment.reminderRate.amount | price}}</td></tr>
                                <tr v-if="showingPayment.alreadyPaidAmount"><td>{{$translate('Already paid')}}</td><td>{{showingPayment.alreadyPaidAmount | price}}</td></tr>
                                <tr v-if="showingPayment.credited_amount"><td>{{$translate('Credited amount')}}</td><td>{{showingPayment.credited_amount | price}}</td></tr>
                                <tr v-if="showingPayment.leftToPayWithFees"><td>{{'To pay' | translate}}</td><td class="bold">{{showingPayment.leftToPayWithFees | price}}</td></tr>
                            </table>
                        </v-col>

                        <v-col md=6 cols=12>
                            <ul class="list-unstyled pl-0">
                                <li v-if="showingPayment.status == 'failed' && showingPayment.message"><p>{{showingPayment.message}}</p></li>
                                <li><p class="bold">{{'This payment contains the following products' | translate}}</p></li>
                                <li v-for="(item,index) in showingPayment.items" :key="index">{{item}}</li>
                            </ul>
                        </v-col>
                    </v-row>

                </v-card-text>

                <v-card-actions>
                    <v-btn v-if="showingPayment.canPay" color="primary" @click="pay(showingPayment)">{{ $translate('Pay') }}</v-btn>
                    <v-btn v-if="showingPayment.invoice_hash || showingPayment.reminder_id" color="primary" @click="getInvoice(showingPayment)" :loading="gettingInvoice">{{ $translate('Get invoice') }}</v-btn>
                    <v-spacer />
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { downloadFile, isExpoApp } from '../util';
export default {
    name: 'zoezi-coming-payments',

    zoezi: {
        title: 'Upcoming payments',
        icon: 'mdi-credit-card-clock-outline'
    },

    data: () => ({
        debts: [],
        unsubscribe: null,
        showPayDialog: false,
        showPaymentDialog: false,
        payments: [],
        expandedPanel: 0,
        showingPayment: null,
        gettingInvoice: false
    }),

    props: {
    },

    methods: {
        fetchDebts() {
            this.$api.get('/api/memberapi/debts/get').then(x => {
                this.debts = x.map(d => ({
                    ...d,
                    date: Date.newFull(d.date),
                    dateInfo: d.method == 'directdebit' && d.status != 'failed' ? this.$translate('To be deducted by direct debit') : (d.status == 'pending' ? '' : this.$translate('Due date')),
                    name: d.name ? d.name : d.items.join(', ')
                })).sort((a, b) => Date.newFull(a.date) - Date.newFull(b.date));
                this.$emit('count', this.debts.length);
            });
        },
        getPaymentItems(debt) {
            let payments = [];
            payments.push({
                name: debt.name,
                date: debt.date,
                amount: debt.amount,
                payments: debt.payments,
                method: debt.method,
            });
            if (debt.leftToPay) {
                payments[0].amount = +debt.leftToPay;
            }

            if (debt.reminderFee) {
                payments.push({
                    name: debt.reminderFee.name,
                    amount: debt.reminderFee.amount,
                    payments: debt.reminderFee.payments,
                    date: debt.reminderFee.date,
                });
            }

            if (debt.reminderRate) {
                payments.push({
                    name: debt.reminderRate.name,
                    amount: debt.reminderRate.amount,
                    payments: debt.reminderRate.payments,
                    date: debt.reminderRate.date
                });
            }

            return payments.map(payment => {
                let name = payment.name;

                return {
                    name: name,
                    amount: payment.amount,
                    payments: payment.payments,
                    date: payment.date,
                    method: payment.method
                };
            });
        },
        pay(debt) {
            this.payments= [{payment: {payments: this.getPaymentItems(debt)}}];
            this.showPayDialog = true;
        },
        showPayment(debt) {
            this.showingPayment = debt;
            this.showPaymentDialog = true;
        },
        getMethodName(debt) {
            if (debt.method) {
                let method = debt.method;
                method = debt.method == 'unknown' ? 'Pay on site' : debt.method;
                return this.$translate(method).capitalize();
            }
        },
        formatBankgiroNumber(x) {
            if (x) {
                x = String(x);
                if (x.length == 6) {
                    x = x.insertAt(3, '-');
                } else if (x.length >= 6) {
                    x = x.insertAt(x.length - 4, '-');
                }
            }

            return x;
        },
        getInvoice(debt) {
            this.gettingInvoice = true;
            let url, args;
            if (debt.reminder_id) {
                url = '/api/memberapi/invoice/reminder/get';
                args = {id: debt.reminder_id};
            } else {
                url = '/api/memberapi/invoice/get';
                args = {hash: debt.invoice_hash};
            }

            if (isExpoApp()) {
                let data = { api: { url: 'current', path: url, args }, type: 'pdf' };
                window.ReactNativeWebView.postMessage(JSON.stringify({type: 'openFile', data}));
                this.gettingInvoice = false;
            } else {
                this.$api.post(url, null, args).then(response => {
                    if (response.ok) {
                        response.blob().then(x => {
                            downloadFile({filename: `${this.$translate('Invoice')}.pdf`, data: x, type: 'application/pdf'});
                        });
                    }
                    this.gettingInvoice = false;
                });
            }

        }
    },

    mounted() {
        this.fetchDebts();
        this.unsubscribe = this.$root.$api.subscribe({type: 'payment', callback: () => this.fetchDebts()})
    },
    destroyed() {
        this.unsubscribe?.();
    }
}
</script>
